import { Injectable } from '@angular/core';
import { GatewayResponse,User2CommissionByUserGetRequestPaged } from '@dave/types';
import { Actions,createEffect,ofType } from '@ngrx/effects';
import { Action,Store } from '@ngrx/store';
import { EMPTY, exhaustMap, from, of } from 'rxjs';
import { catchError,concatMap,map,withLatestFrom } from 'rxjs/operators';
import { stringifyIfNotNullOrUndefined } from '../../../helper/helper';
import {
UserToCommissionEntity,
UserToCommissionEntityFromBackend,
UserToCommissionEntityFromGRPCBackend
} from '../../entities/user-to-commission.entity';
import { CommissionResolver } from '../../guards/commission.resolver';
import { AppGatewayService } from '../../services/app-gateway.service';
import { HttpService } from '../../services/http.service';
import { BaseActionTypes } from '../actions/base.actions';
import { UserToCommissionActionTypes } from '../actions/user-to-commission.action';
import { State } from '../index';
import { getToken } from '../selectors/base.selectors';
import { getCommissionById,getCommissionsFetched } from '../selectors/commission.selector';
import { getUserToCommissionDictionary, getUserToCommissions } from '../selectors/user-to-commission.selector';


enum ErrorCodes {
    Load = 'UserToCommission Abrufen fehlgeschlagen',
    Modify = 'UserToCommission Bearbeiten fehlgeschlagen',
    Remove = 'UserToCommission Löschen fehlgeschlagen',
    Add = 'Berechtigung zum Hinzufügen des Nutzers nicht ausreichend',
}

@Injectable()
export class UserToCommissionEffects {
    ModifyUserToCommission$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserToCommissionActionTypes.Change),
            withLatestFrom(this.store$.select(getToken)),
            concatMap(([{ Payload }, token]) => {
                const queryString = `
            mutation {
                changeUser2Commission(
                    id: ${Payload.id}
                    ${stringifyIfNotNullOrUndefined(Payload, 'additionalData')}
                ) {
                    ${UserToCommissionEntity.GqlFields}
                }
            }`;

                return this.gatewayHttpService.graphQl({ query: queryString }, { token }).pipe(
                    concatMap((res) => {
                        const ret: Action[] = [];
                        if (res && res.changeUser2Commission) {
                            ret.push(
                                UserToCommissionActionTypes.UpdateOne({
                                    Payload: UserToCommissionEntityFromBackend(res.changeUser2Commission[0]), // todo fix in be, should not be an array
                                }),
                            );
                        } else {
                            ret.push(BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: ErrorCodes.Modify } }));
                        }
                        return ret;
                    }),
                    catchError((err, caught) =>
                        of(
                            BaseActionTypes.ErrorAction({
                                Payload: {
                                    ToasterMessage: ErrorCodes.Modify,
                                    Err: err,
                                    Caught: caught,
                                },
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    AddUserToCommission$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserToCommissionActionTypes.Create),
            withLatestFrom(this.store$.select(getToken)),
            concatMap(([{ Payload }, token]) => {
                const queryString = `
            mutation {
                createUser2Commission(
                    ${stringifyIfNotNullOrUndefined(Payload, 'userId')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'commissionId')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'assignee')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'additionalData')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'fromEntity')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'roleId')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'fromId')}
                ) {
                    ${UserToCommissionEntity.GqlFields}
                }
            }`;

                return this.gatewayHttpService.graphQl({ query: queryString }, { token }).pipe(
                    withLatestFrom(this.store$.select(getCommissionById({ id: Payload.commissionId })), this.store$.select(getUserToCommissions)),
                    concatMap(([res, commission, u2c]) => {
                        const ret: Action[] = [];
                        if (res && res.createUser2Commission) {
                            ret.push(
                                UserToCommissionActionTypes.UpdateAll({
                                    Payload: [...u2c.filter((v) => v.CommissionId !== res.createUser2Commission[0].commissionId), ...res.createUser2Commission.map((v) => UserToCommissionEntityFromBackend(v))],
                                }),
                            );
                            if (commission) {
                                this.commissionResolver.pollUpdated();
                            }
                        } else {
                            ret.push(BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: ErrorCodes.Add } }));
                        }
                        return ret;
                    }),
                    catchError((err, caught) =>
                        of(
                            BaseActionTypes.ErrorAction({
                                Payload: {
                                    ToasterMessage: ErrorCodes.Add,
                                    Err: err,
                                    Caught: caught,
                                },
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    RemoveUserToCommission$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserToCommissionActionTypes.Delete),
            withLatestFrom(this.store$.select(getToken)),
            concatMap(([{ Payload }, token]) => {
                const queryString = `
            mutation {
                deleteUser2Commission(
                    id: ${Payload.id}
                ) {
                    ${UserToCommissionEntity.GqlFields}
                }
            }`;

                return this.gatewayHttpService.graphQl({ query: queryString }, { token }).pipe(
                    withLatestFrom(this.store$.select(getCommissionsFetched), this.store$.select(getUserToCommissions)),
                    concatMap(([res, commissionsFetched, u2c]) => {
                        const ret: Action[] = [];
                        if (res && res.deleteUser2Commission) {
                            ret.push(
                                UserToCommissionActionTypes.UpdateAll({
                                    Payload: [...u2c.filter((v) => v.CommissionId !== res.deleteUser2Commission[0].commissionId), ...res.deleteUser2Commission.map((v) => UserToCommissionEntityFromBackend(v))],
                                }),
                            );
                            if (commissionsFetched) {
                                this.commissionResolver.pollUpdated();
                            }
                        } else {
                            ret.push(BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: ErrorCodes.Remove } }));
                        }
                        return ret;
                    }),
                    catchError((err, caught) =>
                        of(
                            BaseActionTypes.ErrorAction({
                                Payload: {
                                    ToasterMessage: ErrorCodes.Remove,
                                    Err: err,
                                    Caught: caught,
                                },
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    // GetUserToCommission$ = createEffect(
    //     () =>
    //         this.actions$.pipe(
    //             ofType(UserToCommissionActionTypes.Load),
    //             withLatestFrom(this.store$.select(getToken)),
    //             concatMap(([{ Payload }, token]) => {
    //                 const queryString = `
    //         query {
    //             user2Commission${Payload?.updatedSince ? `(updatedSince: "${Payload.updatedSince}")` : ''} {
    //                 ${UserToCommissionEntity.GqlFields}
    //             }
    //         }`;
    //                 return this.gatewayHttpService.graphQl({ query: queryString }, { token, retry: true }).pipe(
    //                     tap((res) => {
    //                         if (!(Payload?.updatedSince && !res.user2Commission.length)) {
    //                             this.store$.dispatch(
    //                                 res && res.user2Commission
    //                                     ? Payload?.updatedSince
    //                                         ? UserToCommissionActionTypes.UpdateMany({
    //                                             Payload: res.user2Commission.map((val) => UserToCommissionEntityFromBackend(val)),
    //                                             updateLatestUpdatedAt: true,
    //                                         })
    //                                         : UserToCommissionActionTypes.UpdateAll({
    //                                             Payload: res.user2Commission.map((val) => UserToCommissionEntityFromBackend(val)),
    //                                             updateLatestUpdatedAt: true,
    //                                         })
    //                                     : BaseActionTypes.ErrorAction({
    //                                         Payload: { ToasterMessage: ErrorCodes.Load },
    //                                     }),
    //                             );
    //                         }
    //                     }),
    //                     catchError((err, caught) => {
    //                         this.store$.dispatch(
    //                             BaseActionTypes.ErrorAction({
    //                                 Payload: {
    //                                     ToasterMessage: ErrorCodes.Load,
    //                                     Err: err,
    //                                     Caught: caught,
    //                                 },
    //                             }),
    //                         );
    //                         return EMPTY;
    //                     }),
    //                 );
    //             }),
    //         ),
    //     { dispatch: false },
    // );

    GetUserToCommission$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(UserToCommissionActionTypes.Load),
                exhaustMap(({ Payload }) =>
                    from(this.getRequest()).pipe(
                        withLatestFrom(this.store$.select(getUserToCommissionDictionary), this.store$.select(getUserToCommissions).pipe(map(u2c => u2c.length))),
                        map(([entities, u2cDict, u2cLength]) => {
                            if (u2cLength !== entities.length || entities.some(e => e.UpdatedAt.getTime() !== u2cDict[e.Id]?.UpdatedAt.getTime())) {
                                this.store$.dispatch(UserToCommissionActionTypes.UpdateAll({
                                    Payload: entities,
                                    updateLatestUpdatedAt: true,
                                }));
                            }
                        },
                        ),
                        catchError((err, caught) => {
                            this.store$.dispatch(
                                BaseActionTypes.ErrorAction({
                                    Payload: {
                                        ToasterMessage: ErrorCodes.Load,
                                        Err: err,
                                        Caught: caught,
                                    },
                                }),
                            );
                            return EMPTY;
                        }),
                    ),
                ),
            ),
        { dispatch: false },
    );



    private getRequest(): Promise<UserToCommissionEntity[]> {
        const requestPayload: Omit<User2CommissionByUserGetRequestPaged, 'Page'> = {
        };

        const requests$ = new Promise<{
            AuthUser2CommissionByUserGetPaged: Partial<GatewayResponse['AuthUser2CommissionByUserGetPaged']>;
            Errors: GatewayResponse['Errors'];
        }>(async (resolve, reject) => {
            let _offsetId: number | null = null;
            const results: GatewayResponse['AuthUser2CommissionByUserGetPaged']['User2CommissionResponses'] = [];
            let errors = {};
            let lastPageReached = false;

            const getPage = async (offsetId: number | null) => {


                if (offsetId) {
                    requestPayload.OffsetId = offsetId.toString()
                }
                const res = await this.gatewayService.Request({
                    AuthUser2CommissionByUserGetPaged: requestPayload,
                });

                const responses = res.AuthUser2CommissionByUserGetPaged?.User2CommissionResponses || [];


                if (responses.length) {
                    const CHUNK_SIZE = 10000;
                    for (let i = 0; i < responses.length; i += CHUNK_SIZE) {
                        const chunk = responses.slice(i, i + CHUNK_SIZE);
                        results.push(...chunk);
                    }

                    offsetId = res.AuthUser2CommissionByUserGetPaged.LastId && +res.AuthUser2CommissionByUserGetPaged.LastId;
                }
                lastPageReached = !!res.AuthUser2CommissionByUserGetPaged?.LastPage;

                if (res.Errors) {
                    errors = { ...errors, ...res.Errors };
                }

                return offsetId;
            };

            while (!lastPageReached) {
                try {
                    const pageLastId = await getPage(_offsetId);
                    if (pageLastId && _offsetId === pageLastId) {
                        throw new Error('LastId is the same as offsetId');
                    }
                    _offsetId = pageLastId;
                } catch (err) {
                    return reject(err);
                }

            }

            resolve({
                AuthUser2CommissionByUserGetPaged: { User2CommissionResponses: results },
                Errors: errors,
            });
        });
        return requests$
            .then((res) => {
                if (Object.keys(res?.Errors || {}).length === 0) {
                    return res.AuthUser2CommissionByUserGetPaged.User2CommissionResponses.map(UserToCommissionEntityFromGRPCBackend);
                } else {
                    throw res.Errors;
                }
            });
    }

    constructor(private actions$: Actions, private store$: Store<State>, private gatewayHttpService: HttpService, private gatewayService: AppGatewayService, private commissionResolver: CommissionResolver) {}
}
